import React, { useState } from "react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { Button } from "antd"
import i18n from "i18n"
import PropTypes from "prop-types"
import { Link } from "react-router-dom"
import { routes } from "router/routes"
import { Text, Title } from "ui/Typography"
import { FilterIcon } from "ui/Icon/DSIcon/Filter"
import { LeftArrow } from "ui/Icon/DSIcon/LeftArrow"
import { Rotate } from "ui/Icon/DSIcon/Rotate"
import SendClientMessageModal from "ui/SendClientMessageModal"
import styles from "./ChatBox.module.css"

// An entity the assistant named in its answer, written as an ordinary markdown link:
// [Sarah Jones](#entity/client/123). The id always comes from the record the backend
// actually read, never from the model, so a link either resolves or is not written.
//
// A FRAGMENT, not a custom scheme: react-markdown sanitises hrefs against a protocol
// allowlist and rewrites anything else to `javascript:void(0)`, which turned every
// entity link into a dead anchor opening about:blank. Fragments pass through untouched.
const ENTITY_HREF = /^#entity\/([a-z]+)\/(.+)$/

// Bookings are keyed by UUID and everything else by integer id — the backend sends
// whichever this screen takes.
const ENTITY_ROUTES = {
  client: (id) => routes.guideClientPath({ id }),
  // The reservation screen, not the older booking-details one: it's where the Bookings
  // landing (Reservations list) opens a booking, and it takes the same booking UUID.
  booking: (id) => routes.guideReservationDetailPath({ id }),
  crew: (id) => routes.guideCrewMemberDetailsPath({ id }),
  staff: (id) => routes.proStaffDetailsPath({ id }),
  resource: (id) => routes.guideEquipmentDetailsPath({ id }),
  // An experience has no read-only screen of its own, so this opens the builder — on
  // the DETAILS tab (title, description, categories), which is what someone clicking
  // an experience's name wants to see. Not the preview step the experiences table uses.
  experience: (id) => routes.proEditExperienceV2Path({ id }, { step: "details" })
}

export function entityPath(type, id) {
  return ENTITY_ROUTES[type] ? ENTITY_ROUTES[type](id) : null
}

// Two link treatments, per the Figma booking-listing reference: the experience/trip
// title is brand-blue with no underline (it's already bold from the answer's own
// markdown); everything else a guide can click — client, booking number, staff,
// resource — is underlined in the surrounding text's own color, not blue, so it reads
// as "this is clickable" without competing with the one accent color on the card.
const NEUTRAL_LINK_TYPES = new Set(["client", "booking", "staff", "resource"])

function entityLinkClassName(type) {
  return NEUTRAL_LINK_TYPES.has(type) ? styles.entityLinkNeutral : styles.entityLink
}

// Plain text of rendered markdown children — the link text may arrive wrapped (**bold**).
function childrenText(children) {
  return React.Children.toArray(children)
    .map((child) => (typeof child === "string" || typeof child === "number" ? child : childrenText(child.props?.children)))
    .join("")
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// A client's email, keyed by their integer users.id — opens the send-email modal in
// place rather than a screen. The send goes by userId; the text is display only (the
// recipient chip falls back to it, having no name), so anything that isn't an email, or
// an id the form can't send by, renders as plain text rather than a modal that can't send.
function EmailLink({ userId, children }) {
  const [isOpen, setIsOpen] = useState(false)
  // Mounted on first click, not per link — an answer listing twenty clients would
  // otherwise mount twenty modals and their store subscriptions. Kept mounted after
  // so it can animate closed.
  const [isMounted, setIsMounted] = useState(false)
  const email = childrenText(children).trim()
  if (!EMAIL_PATTERN.test(email) || !/^\d+$/.test(userId)) return <>{children}</>
  const handleClick = (e) => {
    e.preventDefault()
    setIsMounted(true)
    setIsOpen(true)
  }
  return (
    <>
      <a href={`mailto:${email}`} className={styles.entityLinkNeutral} onClick={handleClick}>
        {children}
      </a>
      {isMounted && (
        <SendClientMessageModal
          open={isOpen}
          type="email"
          onClose={() => setIsOpen(false)}
          recipients={[{ userId, user: { email } }]}
          setSelectedRowKeys={() => {}}
          redirectOnClose={false}
        />
      )}
    </>
  )
}

EmailLink.propTypes = {
  userId: PropTypes.string.isRequired,
  children: PropTypes.node
}

function MessageLink({ href, children }) {
  const match = ENTITY_HREF.exec(href || "")
  if (match && match[1] === "email") return <EmailLink userId={decodeURIComponent(match[2])}>{children}</EmailLink>
  const to = match && entityPath(match[1], decodeURIComponent(match[2]))
  // A <Link>, not a click handler, so cmd/middle-click still opens a new tab.
  if (to) return <Link className={entityLinkClassName(match[1])} to={to}>{children}</Link>
  // Anything else — an entity type this build doesn't know, or a URL react-markdown
  // rewrote to javascript:void(0) — renders as plain text. Opening those was how a
  // sanitised link became a blank tab.
  if (!/^(https?:|mailto:|tel:)/i.test(href || "")) return <>{children}</>
  // A plain mailto:/tel: link — the email/phone in a client line, not an entity of its
  // own — gets the same neutral treatment as the name next to it, for the same reason.
  // No target="_blank": those hand off to a mail/dialer app, and some browsers open and
  // leave behind an empty tab on the way. Only a real http(s) link opens in a new one.
  const external = /^https?:/i.test(href)
  return (
    <a
      href={href}
      className={styles.entityLinkNeutral}
      {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
    >
      {children}
    </a>
  )
}

MessageLink.propTypes = {
  href: PropTypes.string,
  children: PropTypes.node
}

// Navigation, not answers: these sit in their own row below a divider, separated
// from whatever topic buttons came with the same message. Everything else — the
// choices the question is actually asking about — stays in the row above it.
// These stay raw English: they're matched against option text the API/LLM sends,
// not shown to the user directly (see NAV_OPTION_I18N_KEYS for the display copy).
const NAV_OPTION_LABELS = new Set(["Filter Results", "Try Again", "Step Back", "Open Main Menu"])

// Translation keys for the same labels, keyed off the raw match value above.
const NAV_OPTION_I18N_KEYS = {
  "Filter Results": "ai_assistant.nav.filter_results",
  "Try Again": "ai_assistant.nav.try_again",
  "Step Back": "ai_assistant.nav.step_back",
  "Open Main Menu": "ai_assistant.nav.open_main_menu"
}

// Icons are a per-label choice — not every nav option has one.
const OPTION_ICONS = {
  "Filter Results": FilterIcon,
  "Try Again": Rotate,
  "Step Back": LeftArrow
}

// Handled entirely in the browser (ChatBox reveals the default prompts below the
// divider) rather than sent as a message — it opens a menu, it does not ask the
// assistant anything.
export const MAIN_MENU_OPTION = "Open Main Menu"

// Splits one AI message into the prose to render and the buttons it offers: the
// numbered choices written into the text, plus whatever came in the options array.
function parseMessage(message) {
  const { content: rawContent = "", options = [] } = message ?? {}
  const normalized = rawContent.replaceAll(String.raw`\n`, "\n")

  // Strip out numbered option lines and options header so we can render them as buttons
  const optionLines = []
  const mdLines = []
  let inOptionsBlock = false

  normalized.split("\n").forEach((line) => {
    const trimmed = line.trim()
    if (/^(\*{1,2})?(options|choose one|select one|choose|please select)(\*{1,2})?:?$/i.test(trimmed)) {
      inOptionsBlock = true
      return
    }
    const numberedMatch = trimmed.match(/^\d+[.)]\s+(.+)/)
    if (inOptionsBlock && numberedMatch) {
      optionLines.push(numberedMatch[1])
      return
    }
    if (inOptionsBlock && trimmed !== "" && !numberedMatch) {
      inOptionsBlock = false
    }
    mdLines.push(line)
  })

  const all = [...optionLines, ...(Array.isArray(options) ? options : [])]
  return {
    markdownContent: mdLines.join("\n").replaceAll(/^•\s*/gm, "- "),
    topicOptions: all.filter((opt) => !NAV_OPTION_LABELS.has(opt)),
    navOptions: all.filter((opt) => NAV_OPTION_LABELS.has(opt))
  }
}

// The navigation buttons a message offers. ChatBox draws these in the footer above
// the composer (below a full-width divider, centred) rather than in the message
// itself, per the Figma — so they sit apart from the question's own answers.
export function navOptionsOf(message) {
  return parseMessage(message).navOptions
}

export function renderAIMessage(message, onOptionClick, isLatest = false) {
  const { id: msgId, optionSelected = false } = message ?? {}
  const { markdownContent, topicOptions } = parseMessage(message)
  const selectedOption = optionSelected || null
  // Options are only interactive on the latest AI message; all previous ones are disabled
  const optionsDisabled = !isLatest || !!selectedOption

  return (
    <>
      <ReactMarkdown
        className={styles.markdownBody}
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <Text className={styles.mdParagraph}>{children}</Text>,
          h2: ({ children }) => (
            <Title level={5} className={styles.mdHeading2}>
              {children}
            </Title>
          ),
          h3: ({ children }) => (
            <Title level={5} className={styles.mdHeading3}>
              {children}
            </Title>
          ),
          ul: ({ children }) => <ul className={styles.mdList}>{children}</ul>,
          li: ({ children }) => <li className={styles.mdListItem}>{children}</li>,
          strong: ({ children }) => <Text fw="600">{children}</Text>,
          a: MessageLink,
        }}
      >
        {markdownContent}
      </ReactMarkdown>
      {topicOptions.length > 0 && renderOptionButtons(topicOptions, msgId, "opts", onOptionClick, selectedOption, optionsDisabled)}
    </>
  )
}

export function renderOptionButtons(opts, msgId, key, onOptionClick, selectedOption, isDisabled, rowClass) {
  // A single option button spans the full row per Figma, rather than sitting at its
  // own compact width — doesn't apply to the centred nav row, or when there's more
  // than one (e.g. the three drill-down buttons), which Figma has no screen for.
  const fillWidth = opts.length === 1 && !rowClass
  return (
    <div key={`${msgId}-${key}`} className={[styles.optionButtons, rowClass].filter(Boolean).join(" ")}>
      {opts.map((opt, i) => {
        const isSelected = opt === selectedOption
        const Icon = OPTION_ICONS[opt]
        const label = NAV_OPTION_I18N_KEYS[opt] ? i18n.t(NAV_OPTION_I18N_KEYS[opt]) : opt
        return (
          <Button
            key={`${msgId}-${key}-${i}`}
            color="primary"
            variant="outlined"
            disabled={isDisabled}
            className={[
              "py-xs px-l",
              styles.optionButton,
              isSelected && styles.optionButtonSelected,
              fillWidth && styles.optionButtonFull
            ].filter(Boolean).join(" ")}
            onClick={() => onOptionClick?.(opt)}
            style={{ opacity: isDisabled && !isSelected ? 0.45 : 1 }}
          >
            {Icon && (
              <span aria-hidden="true" className={styles.optionButtonIcon}>
                <Icon width={14} height={14} />
              </span>
            )}
            {label}
          </Button>
        )
      })}
    </div>
  )
}
